import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DASHBOARDS_GROUP,
  getRouteTransitionGroup,
} from '../utils/getRouteTransitionGroup';

interface RouteTransitionState {
  // True while a route transition session is open.
  isTransitioning: boolean;
  // True while a page has explicitly requested the overlay (form / FormInit
  // loading) — shown by the same persistent overlay, independent of navigation.
  isOverlayRequested: boolean;
  // The active page reports navigation loading via this ref-counted pair so a
  // transition session stays open until the destination (incl. a redirect
  // target) is ready.
  requestBusy: () => void;
  releaseBusy: () => void;
  // Pages force the overlay on (without a navigation) via this ref-counted pair.
  requestOverlay: () => void;
  releaseOverlay: () => void;
}

const useRouteTransition = (): RouteTransitionState => {
  const { pathname } = useLocation();
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [busyCount, setBusyCount] = useState<number>(0);
  const [overlayCount, setOverlayCount] = useState<number>(0);

  const prevGroupRef = useRef<string | null>(null); // null until first mount
  const sessionRef = useRef<boolean>(false); // a transition session is open
  const busyCountRef = useRef<number>(0);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  busyCountRef.current = busyCount;

  // Detect the group change DURING render so isTransitioning flips in the same
  // commit as the navigation — an effect runs after paint, which let the stale
  // grid (e.g. a peerset switch on the same mounted page) flash first. Guarded
  // so it only sets state on a real change → no render loop.
  const group = getRouteTransitionGroup(pathname);
  if (prevGroupRef.current === null) {
    prevGroupRef.current = group; // initial mount, no transition
  } else if (prevGroupRef.current !== group) {
    prevGroupRef.current = group;
    // Going TO dashboards never transitions (group is remembered above so
    // leaving it later does). Row-to-row / subset switches share a group, so
    // they fall through here as a no-op.
    if (group !== DASHBOARDS_GROUP && !isTransitioning) {
      sessionRef.current = true;
      setIsTransitioning(true);
    }
  }

  const requestBusy = useCallback(() => setBusyCount((c) => c + 1), []);
  const releaseBusy = useCallback(
    () => setBusyCount((c) => Math.max(0, c - 1)),
    []
  );
  const requestOverlay = useCallback(() => setOverlayCount((c) => c + 1), []);
  const releaseOverlay = useCallback(
    () => setOverlayCount((c) => Math.max(0, c - 1)),
    []
  );

  // Close the session once the destination stops loading, keeping the minimum
  // visible window. A redirect target re-arms via its own busy report, and the
  // timer re-checks busy before hiding to absorb the handoff.
  useEffect(() => {
    const clearHide = () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };

    if (!sessionRef.current) {
      return;
    }

    // Still loading (incl. the destination of a redirect) → keep covering.
    if (busyCount > 0) {
      clearHide();
      return;
    }

    // Hide as soon as loading finishes. A zero timeout still lets a redirect
    // target re-arm via its own busy report before the overlay tears down.
    clearHide();
    hideTimeoutRef.current = setTimeout(() => {
      // A redirect target may have started loading after this was scheduled.
      if (busyCountRef.current > 0) {
        return;
      }
      sessionRef.current = false;
      setIsTransitioning(false);
    }, 250);
  }, [isTransitioning, busyCount]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return {
    isTransitioning,
    isOverlayRequested: overlayCount > 0,
    requestBusy,
    releaseBusy,
    requestOverlay,
    releaseOverlay,
  };
};

export default useRouteTransition;
